import type { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';

export type BaseQueryArg = {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  params?: any;
  body?: any;
  headers?: AxiosRequestConfig['headers'];
  onUploadProgress?: AxiosRequestConfig['onUploadProgress'];
};

export type ApiError = {
  status: number | 'CANCELLED' | 'FETCH_ERROR';
  message: string;
  data?: unknown;
};

export type SerializedError = {
  message?: string;
  code?: string;
  name?: string;
  stack?: string;
};

export type RTKQueryError = ApiError | SerializedError;

export type Request = Record<string, unknown> | undefined;
export type Response<T> = { success: boolean; message: string; data?: T };
export type Paginated<T> = { count: number; total: number; rows: T[] };

export type WithRefetchKey = { __refetchKey?: number };

const rtkBaseQueryWrapper =
  ({ baseUrl = '' }: { baseUrl?: string } = {}): BaseQueryFn<BaseQueryArg, unknown, ApiError> =>
  async ({ url, method = 'GET', params, body, headers, onUploadProgress }, api) => {
    try {
      const result = await axios.request({
        url: baseUrl + url,
        method,
        params,
        data: body,
        headers,
        signal: api.signal as AbortSignal,
        onUploadProgress,
      });

      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      const status = err.code === 'ERR_CANCELLED' ? 'CANCELLED' : (err.response?.status ?? 'FETCH_ERROR');

      const errorObj: ApiError = {
        status,
        message: extractAxiosErrorMessage(err),
        data: err.response?.data,
      };

      return { error: errorObj };
    }
  };

const unwrap = <T>(payload: unknown): T => {
  if (payload && typeof payload === 'object' && 'data' in (payload as any)) {
    return (payload as { data: T }).data;
  }
  return payload as T;
};

/**
 * Internal helper to extract error message from Axios errors
 */
const extractAxiosErrorMessage = (error: AxiosError): string => {
  const data = error.response?.data;
  if (data && typeof data === 'object' && 'message' in data) {
    return (data as { message: string }).message;
  }
  return error.message ?? 'An unknown error occurred';
};

/**
 * Extracts a user-friendly error message from RTK Query errors.
 * Handles both FetchBaseQueryError (our ApiError) and SerializedError formats.
 */
const getErrorMessage = (error: unknown): string => {
  if (!error) return 'An unknown error occurred';

  // Handle our ApiError type (FetchBaseQueryError)
  if (typeof error === 'object' && 'status' in error && 'message' in error) {
    const apiError = error as ApiError;
    return apiError.message || `Request failed with status ${apiError.status}`;
  }

  // Handle SerializedError
  if (typeof error === 'object' && 'message' in error) {
    const serializedError = error as SerializedError;
    return serializedError.message || 'An unknown error occurred';
  }

  return 'An unknown error occurred';
};

export { rtkBaseQueryWrapper, unwrap, getErrorMessage };
